import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listProposals, deleteProposal, updateProposalChecked } from '../lib/firebase';
import type { ProposalListItem } from '../lib/firebase';
import '../styles/landing.css';

const CHANNEL_LABELS = ['네이버', '인스타', '메타', '유튜브', '구글', '의료'];
const CHANNEL_KEYS = ['naver', 'insta', 'meta', 'yt', 'google', 'med'];

function scoreColor(s: number) {
  if (s >= 70) return 'var(--l-green)';
  if (s >= 40) return 'var(--l-orange)';
  return 'var(--l-red)';
}

function scoreClass(s: number) {
  if (s >= 70) return 'score-high';
  if (s >= 40) return 'score-mid';
  return 'score-low';
}

function avgScore(scores?: Record<string, number>): number {
  if (!scores) return 0;
  const vals = CHANNEL_KEYS.map(k => scores[k] || 0);
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

function formatDate(iso: string) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [history, setHistory] = useState<ProposalListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    listProposals()
      .then(list => setHistory(list))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!confirm('이 제안서를 삭제하시겠습니까?')) return;
    try {
      await deleteProposal(id);
      setHistory(prev => prev.filter(h => h.id !== id));
    } catch { /* ignore */ }
  };

  const handleToggleChecked = async (e: React.MouseEvent, id: string, current: boolean) => {
    e.stopPropagation();
    const newVal = !current;
    setHistory(prev => prev.map(h => h.id === id ? { ...h, checked: newVal } : h));
    try {
      await updateProposalChecked(id, newVal);
    } catch { /* revert on failure */
      setHistory(prev => prev.map(h => h.id === id ? { ...h, checked: current } : h));
    }
  };

  return (
    <main className="landing">
      {/* Hero */}
      <div className="l-hero">
        <div className="l-hero-glow" />
        <div className="l-logo-area">
          <div className="l-logo-icon">📊</div>
          <div className="l-logo-text">마케팅 진단 리포트</div>
        </div>

        <h1 className="l-h1">
          병원 마케팅 현황을<br />
          <em>3분 만에</em> 진단하세요
        </h1>

        <p className="l-sub">
          네이버, 인스타그램, 유튜브, 구글 등<br />
          45개 항목을 체크하고 즉시 점수를 확인하세요
        </p>

        <button className="l-cta" onClick={() => navigate('/create')}>
          제안서 만들기 <span className="l-cta-arrow">→</span>
        </button>

        <div className="l-stats">
          <div className="l-stat"><div className="l-stat-num">45</div><div className="l-stat-label">체크 항목</div></div>
          <div className="l-stat"><div className="l-stat-num">6</div><div className="l-stat-label">매체 분석</div></div>
          <div className="l-stat"><div className="l-stat-num">3분</div><div className="l-stat-label">소요 시간</div></div>
        </div>

        <div className="l-credit">Developed by <strong>이유빈</strong></div>
      </div>

      {/* Features */}
      <div className="l-features">
        <div className="l-feature-card">
          <div className="l-feature-icon">📋</div>
          <h2>시트 복사 → 붙여넣기</h2>
          <p>엑셀이나 구글시트에서<br />한번에 데이터 입력</p>
        </div>
        <div className="l-feature-card">
          <div className="l-feature-icon">📈</div>
          <h2>자동 점수 계산</h2>
          <p>기준값 대비 점수를<br />매체별로 자동 집계</p>
        </div>
        <div className="l-feature-card">
          <div className="l-feature-icon">📄</div>
          <h2>제안서 PDF 출력</h2>
          <p>진단 결과를 제안서로<br />바로 다운로드</p>
        </div>
      </div>

      {/* History */}
      <div className="l-history">
        <div className="l-history-header">
          <h2>📂 제안서 히스토리</h2>
          <span className="l-history-count">
            총 {query ? `${history.filter(h => {
              const q = query.trim().toLowerCase();
              return (h.hospname || '').toLowerCase().includes(q) || (h.author || '').toLowerCase().includes(q);
            }).length}/` : ''}{history.length}건
          </span>
        </div>

        <div className="l-history-search">
          <input
            type="text"
            placeholder="🔍 병원명 또는 담당자 이름으로 검색"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {query && (
            <button className="l-history-search-clear" onClick={() => setQuery('')} title="지우기">✕</button>
          )}
        </div>

        {(() => {
          const q = query.trim().toLowerCase();
          const filtered = q
            ? history.filter(h =>
                (h.hospname || '').toLowerCase().includes(q) ||
                (h.author || '').toLowerCase().includes(q))
            : history;
          if (loading) return <div className="l-history-empty">불러오는 중...</div>;
          if (history.length === 0) return <div className="l-history-empty">아직 작성한 제안서가 없습니다. 첫 제안서를 만들어보세요!</div>;
          if (filtered.length === 0) return <div className="l-history-empty">"{query}"에 해당하는 제안서가 없습니다.</div>;
          return (
          <div className="l-history-list">
            {filtered.map(item => {
              const avg = avgScore(item.scores);
              const checked = item.totalChecked ?? 0;
              return (
                <div key={item.id} className={`l-history-card${item.checked ? ' l-history-checked' : ''}`} onClick={() => navigate(`/proposal/${item.id}`)}>
                  <div
                    className={`l-history-checkbox ${item.checked ? 'checked' : ''}`}
                    onClick={(e) => handleToggleChecked(e, item.id, !!item.checked)}
                    title={item.checked ? '처리 완료' : '처리 전'}
                  >
                    {item.checked ? '✓' : ''}
                  </div>
                  <div className={`l-score-circle ${scoreClass(avg)}`}>{avg}</div>
                  <div className="l-history-info">
                    <div className="l-history-name">{item.hospname || '(이름 없음)'}</div>
                    <div className="l-history-meta">
                      <span>📅 {formatDate(item.savedAt)}</span>
                      <span>📊 45개 중 {checked}개 충족</span>
                      {item.author && <span>👤 {item.author}</span>}
                    </div>
                  </div>
                  <div className="l-score-bars">
                    {CHANNEL_KEYS.map((k, i) => {
                      const s = item.scores?.[k] || 0;
                      return (
                        <div key={k} className="l-bar" title={`${CHANNEL_LABELS[i]} ${s}%`}>
                          <div className="l-bar-fill" style={{ height: `${s}%`, background: scoreColor(s) }} />
                        </div>
                      );
                    })}
                  </div>
                  <button className="l-history-del" onClick={(e) => handleDelete(e, item.id)} title="삭제">✕</button>
                  <div className="l-history-arrow">→</div>
                </div>
              );
            })}
          </div>
          );
        })()}
      </div>

      {/* Footer */}
      <div className="l-footer">
        &copy; 2026 마케팅 진단 리포트. All rights reserved. | Developed with ❤️ by 이유빈
      </div>
    </main>
  );
}
